import * as THREE from 'three';
import { sampleProfile } from './profileGeometry.js';
import { resolveWindowLayout, defaultWindowLayout } from './windowLayout.js';
import { resolveLeafGrooves, grooveVertexColours } from './leafGrooves.js';
import { leafGroovesForFamily } from './timberLeafSurface.js';
import { leafDimensions } from './leafDimensions.js';
import { resolveWaternose, waternoseSolid, waternoseLeafTop } from './waternoseGeometry.js';

export const LEAF_THICKNESS_MM = 60;
export const LEAF_FRONT_MM = 7.5;
export const LEAF_BACK_MM = LEAF_FRONT_MM - LEAF_THICKNESS_MM;

// Cross sections measured from the Painted surfaces of 60mm_DoorLeft and
// 60mm_DoorBottom in DoorDynamic.glb (2026-10-30). Coordinates are
// [distance inward from the outer edge, Z], in mm, from back to front.
// The side profile also applies to the top; the bottom has two seal grooves.
// These contours describe the material boundary, not the GLB shadow overlays.
export const SIDE_PROFILE_MM = [
  [1.895, -52.5], [1.287, -52.445], [0.795, -52.278],
  [0.421, -52.001], [0.164, -51.613], [0.023, -51.113],
  [0, -50.503], [1.95, -13.498], [2.038, -12.887],
  [2.231, -12.388], [2.529, -12], [2.933, -11.722],
  [3.441, -11.556], [4.056, -11.5], [16.412, -11.5],
  [16.412, -8], [10.912, -8], [10.912, 5.5],
  [10.967, 6.111], [11.134, 6.611], [11.412, 7],
  [11.8, 7.277], [12.3, 7.444], [12.912, 7.5]
];
export const BOTTOM_PROFILE_MM = [
  [2, -52.5], [1.389, -52.444], [0.889, -52.278],
  [0.5, -52], [0.222, -51.611], [0.056, -51.111], [0, -50.5],
  [0, -49], [1.722, -48.701], [3.227, -47.832], [4.344, -46.501],
  [4.938, -44.869], [4.938, -43.131], [4.344, -41.499],
  [3.227, -40.168], [1.722, -39.299], [0, -39], [0, -13.5],
  [0.056, -12.889], [0.222, -12.389], [0.5, -12],
  [0.889, -11.722], [1.389, -11.556], [2, -11.5],
  [21.5, -11.5], [21.5, -8], [16, -8], [16, -6],
  [17.722, -5.698], [19.227, -4.83], [20.344, -3.499],
  [20.938, -1.866], [20.938, -0.129], [20.344, 1.504],
  [19.227, 2.835], [17.722, 3.703], [16, 4.003], [16, 5.5],
  [16.056, 6.111], [16.223, 6.611], [16.5, 7],
  [16.889, 7.278], [17.389, 7.444], [18, 7.5]
];

// Opening sizes stay in mm; the gap scales relative to the reference leaf height.
export const THREE_WINDOWS = { widthMm: 180, heightMm: 180, gapMm: 160, topMm: 220 };

/** @returns {{ xMm: number, yMm: number, widthMm: number, heightMm: number }[]} */
export function leafOpeningsForLayout(layout, widthMm, heightMm) {
  if (!layout || layout === 'none') return [];
  if (layout !== 'three_windows') throw new Error(`Unknown leaf window layout: ${layout}`);
  const { widthMm: w, heightMm: h, gapMm, topMm } = THREE_WINDOWS;
  return resolveWindowLayout({ widthMm, heightMm,
    openings: Array.from({ length: 3 }, () => ({ widthMm: w, heightMm: h })),
    windowLayout: { ...defaultWindowLayout(), gapMm, mainAnchor: { edge: 'start', offsetMm: topMm } }
  }).openings;
}

// Evaluate in millimetres: tiny profile facets lose precision if CSG works
// directly in metre-sized coordinates. Convert the finished mesh to metres.
const EPS_MM = 1;
const MIN_MARGIN_MM = 40;
const CACHE_LIMIT = 12;

export function validateLeafGeometry({ widthMm, heightMm, openings = [], windowLayout, grooves, family, opening, thicknessMm = 60, waternose, cassette,
  sideProfileMm = SIDE_PROFILE_MM, topProfileMm = sideProfileMm, bottomProfileMm = BOTTOM_PROFILE_MM }) {
  const { frontMm, backMm } = leafDimensions({ thicknessMm });
  if (![widthMm, heightMm].every((n) => Number.isFinite(n) && n > MIN_MARGIN_MM * 2)) {
    throw new Error('Leaf dimensions must be finite and greater than 80 mm.');
  }
  sideProfileMm = sampleProfile(sideProfileMm);
  topProfileMm = sampleProfile(topProfileMm);
  bottomProfileMm = sampleProfile(bottomProfileMm);
  [sideProfileMm, topProfileMm, bottomProfileMm].forEach((profile) => {
    if (!Array.isArray(profile) || profile.length < 2 ||
        profile.some((p, i) => !Array.isArray(p) || p.length !== 2 ||
          !p.every(Number.isFinite) || p[0] < 0 || p[0] >= Math.min(widthMm, heightMm) / 2 ||
          p[1] < backMm || p[1] > frontMm ||
          (i > 0 && (p[1] < profile[i - 1][1] ||
            (p[0] === profile[i - 1][0] && p[1] === profile[i - 1][1])))) ||
        profile[0][1] !== backMm || profile[profile.length - 1][1] !== frontMm) {
      throw new Error(`Profile needs 2–200 distinct [inset, Z] points, ordered from Z = ${backMm} to ${frontMm} mm.`);
    }
  });
  const nose = resolveWaternose(waternose, heightMm);
  const leafTopMm = waternoseLeafTop(nose, heightMm, backMm, frontMm);
  openings = resolveWindowLayout({ widthMm, heightMm: leafTopMm, openings, windowLayout }).openings;
  const margin = Math.max(MIN_MARGIN_MM, ...sideProfileMm.map((p) => p[0] + 5),
    ...topProfileMm.map((p) => p[0] + 5), ...bottomProfileMm.map((p) => p[0] + 5));
  const cassetteOverlap = cassette?.overlapMm ?? 10;
  openings.forEach((o, i) => {
    if (!o || ![o.xMm, o.yMm, o.widthMm, o.heightMm].every(Number.isFinite) || o.widthMm <= 0 || o.heightMm <= 0) {
      throw new Error(`Opening ${i + 1} needs valid dimensions and a position in millimetres.`);
    }
    const shortEdges = Object.entries({ left: o.xMm, right: widthMm - o.xMm - o.widthMm,
      top: leafTopMm - o.yMm - o.heightMm, bottom: o.yMm }).filter(([, distance]) => distance < margin);
    if (shortEdges.length) {
      const actual = shortEdges.map(([edge, distance]) => `${edge} ${Math.round(distance * 10) / 10} mm`).join(', ');
      throw new Error(`Opening ${i + 1}: ${actual}. Each edge needs at least ${Math.round(margin * 10) / 10} mm of material. Move or resize the opening.`);
    }
    if (nose.enabled && o.yMm < nose.yMm + Math.max(...nose.profileMm.map(p => p[1])) + cassetteOverlap &&
        o.yMm + o.heightMm > nose.yMm + Math.min(...nose.profileMm.map(p => p[1])) - cassetteOverlap) {
      throw new Error(`Opening ${i + 1} overlaps the waternose or its cassette clearance. Move the opening or waternose.`);
    }
    openings.slice(0, i).forEach((p, j) => {
      if (o.xMm < p.xMm + p.widthMm + MIN_MARGIN_MM &&
          o.xMm + o.widthMm + MIN_MARGIN_MM > p.xMm &&
          o.yMm < p.yMm + p.heightMm + MIN_MARGIN_MM &&
          o.yMm + o.heightMm + MIN_MARGIN_MM > p.yMm) {
        throw new Error(`Openings ${j + 1} and ${i + 1} overlap or have less than 40 mm of material between them. Increase their gap or reduce their size.`);
      }
    });
  });
  return { widthMm, heightMm, leafTopMm, thicknessMm, frontMm, backMm, waternose: nose, openings, sideProfileMm, topProfileMm, bottomProfileMm,
    grooves: resolveLeafGrooves({ widthMm, heightMm: leafTopMm, grooves: leafGroovesForFamily(family, grooves), opening }) };
}

function edgeCutter(Manifold, keep, profile, edge, width, height) {
  const first = profile[0];
  const last = profile[profile.length - 1];
  const points = [
    [-EPS_MM, first[1] - EPS_MM], [first[0], first[1] - EPS_MM],
    ...profile, [last[0], last[1] + EPS_MM],
    [-EPS_MM, last[1] + EPS_MM]
  ];
  const vertical = edge === 'left' || edge === 'right';
  const cutter = keep(Manifold.extrude(points, (vertical ? height : width) + 2 * EPS_MM));
  // Local axes: inward distance, Z, distance along the edge. Manifold also
  // reverses winding for reflections, so all four cutters remain solids.
  const u = edge === 'left' ? [1, 0, 0] : edge === 'right' ? [-1, 0, 0]
    : edge === 'bottom' ? [0, 1, 0] : [0, -1, 0];
  const along = vertical ? [0, 1, 0] : [1, 0, 0];
  const matrix = new THREE.Matrix4().makeBasis(
    new THREE.Vector3(...u), new THREE.Vector3(0, 0, 1), new THREE.Vector3(...along)
  );
  matrix.setPosition(vertical ? (edge === 'right' ? width : 0) : -EPS_MM,
    vertical ? -EPS_MM : (edge === 'top' ? height : 0), 0);
  return keep(cutter.transform(matrix.elements));
}

/**
 * Closed leaf with independent edge profiles, waternose seat and through holes.
 * Origin: bottom-left of the uncut blank, -Z exterior. Caller owns the result.
 * Cache keeps bounded CPU copies; colour, glass and camera changes reuse CSG.
 */
export function createDoorLeafBuilder(Manifold) {
  const cache = new Map();
  return function createDoorLeafGeometry(config) {
    const { widthMm, heightMm, leafTopMm, thicknessMm, frontMm, backMm, waternose, openings, sideProfileMm, topProfileMm, bottomProfileMm, grooves } = validateLeafGeometry(config);
    const key = JSON.stringify([widthMm, heightMm, thicknessMm, waternose, openings, sideProfileMm, topProfileMm, bottomProfileMm, grooves]);
    if (cache.has(key)) {
      const cached = cache.get(key);
      cache.delete(key);
      cache.set(key, cached);
      return cached.clone();
    }
    // Every WASM object, including lazy transformation intermediates, is owned
    // by this call and released once the result has been copied to Three.js.
    const owned = [];
    const keep = (object) => { owned.push(object); return object; };
    try {
      let solid = keep(keep(Manifold.cube([widthMm, leafTopMm, thicknessMm]))
        .translate([0, 0, backMm]));
      ['left', 'right', 'top', 'bottom'].forEach((edge) => {
        const cutter = edgeCutter(Manifold, keep,
          edge === 'bottom' ? bottomProfileMm : edge === 'top' ? topProfileMm : sideProfileMm, edge, widthMm, leafTopMm);
        solid = keep(solid.subtract(cutter));
      });
      if (waternose.enabled) solid = keep(solid.subtract(waternoseSolid(Manifold, keep, waternose, widthMm)));
      openings.forEach((o) => {
        const cutter = keep(keep(Manifold.cube([
          o.widthMm, o.heightMm, thicknessMm + 2 * EPS_MM
        ])).translate([o.xMm, o.yMm, backMm - EPS_MM]));
        solid = keep(solid.subtract(cutter));
      });
      grooves.cuts.forEach((c) => {
        const levels = grooves.localSide === 'both' ? ['front', 'back'] : [grooves.localSide];
        levels.forEach((side) => {
          const z = side === 'back' ? frontMm - grooves.depthMm : backMm - EPS_MM;
          const exact = keep(keep(Manifold.cube([c.widthMm, c.heightMm, grooves.depthMm + EPS_MM]))
            .translate([c.xMm, c.yMm, z]));
          solid = keep(solid.subtract(exact));
        });
      });
      if (solid.status() !== 'NoError' || solid.isEmpty()) {
        throw new Error('Unable to construct the profiled door leaf.');
      }
      const mesh = solid.getMesh();
      const indexed = new THREE.BufferGeometry();
      indexed.setAttribute('position', new THREE.BufferAttribute(new Float32Array(mesh.vertProperties), 3));
      indexed.setIndex(new THREE.BufferAttribute(new Uint32Array(mesh.triVerts), 1));
      const geometry = indexed.toNonIndexed();
      indexed.dispose();
      if (grooves.count) geometry.setAttribute('color', new THREE.BufferAttribute(
        grooveVertexColours(geometry.getAttribute('position'), grooves, frontMm, backMm), 3));
      // Flat normals preserve the small profile facets and avoid shading across
      // the front face / opening reveals. Box projection gives reveals real UVs.
      geometry.scale(0.001, 0.001, 0.001);
      geometry.computeVertexNormals();
      const pos = geometry.getAttribute('position');
      const normal = geometry.getAttribute('normal');
      const uv = new Float32Array(pos.count * 2);
      for (let i = 0; i < pos.count; i++) {
        const nx = Math.abs(normal.getX(i));
        const ny = Math.abs(normal.getY(i));
        const nz = Math.abs(normal.getZ(i));
        uv[i * 2] = nx > nz && nx > ny ? pos.getZ(i) : pos.getX(i);
        uv[i * 2 + 1] = ny > nz && ny > nx ? pos.getZ(i) : pos.getY(i);
      }
      geometry.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
      geometry.computeBoundingBox();
      geometry.computeBoundingSphere();
      cache.set(key, geometry.clone());
      if (cache.size > CACHE_LIMIT) {
        const oldest = cache.keys().next().value;
        cache.get(oldest).dispose();
        cache.delete(oldest);
      }
      return geometry;
    } finally {
      owned.reverse().forEach((object) => object.delete());
    }
  };
}
